
import {computed, defineComponent, ref} from 'vue';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import DateInput from '@/core/components/inputs/DateInput.vue';
import {
  required,
  validSelection,
  endDateShouldBeAfterStartDate,
} from '@/core/util/validation/rules';
import useYearlyReport, {
  type YearlyReportScope,
  YEARLY_REPORT_SCOPE_ADMIN,
} from '@/orangehrmForfaitJoursPlugin/util/composable/useYearlyReport';

export default defineComponent({
  name: 'YearlyReport',
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'date-input': DateInput,
  },
  props: {
    scope: {
      type: String as () => YearlyReportScope,
      required: true,
      validator: (value: YearlyReportScope) =>
        value === YEARLY_REPORT_SCOPE_ADMIN || value === 'manager',
    },
    empNumber: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  setup(props) {
    const yearlyReport = useYearlyReport({
      scope: props.scope,
    });

    const employee = ref<{id: number; label: string} | null>(null);
    const fromDate = ref<string | null>(null);
    const toDate = ref<string | null>(null);

    // Load leave period on mount to set default dates.
    yearlyReport.loadLeavePeriod().then(() => {
      fromDate.value = yearlyReport.leavePeriodStartDate.value;
      toDate.value = yearlyReport.leavePeriodEndDate.value;
    });

    const rules = {
      fromDate: [required],
      toDate: [
        required,
        endDateShouldBeAfterStartDate(() => fromDate.value ?? '', undefined, {
          allowSameDate: true,
        }),
      ],
      employee: [required, validSelection],
    };

    const employeeParams = computed(() => {
      return {includeEmployees: 'onlyCurrent'};
    });

    function monthLabel(year: number, month: number): string {
      return yearlyReport.monthLabel(year, month);
    }

    function onSearch(): void {
      if (!employee.value?.id) {
        return;
      }
      yearlyReport.empNumber.value = employee.value.id;
      yearlyReport.fromDate.value = fromDate.value;
      yearlyReport.toDate.value = toDate.value;
      void yearlyReport.load();
    }

    function onReset(): void {
      employee.value = null;
      fromDate.value = yearlyReport.leavePeriodStartDate.value;
      toDate.value = yearlyReport.leavePeriodEndDate.value;
      yearlyReport.reset();
    }

    return {
      employee,
      fromDate,
      toDate,
      rules,
      employeeParams,
      stats: yearlyReport.stats,
      isLoading: yearlyReport.isLoading,
      error: yearlyReport.error,
      monthLabel,
      onSearch,
      onReset,
    };
  },
});
